import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigation } from 'react-router';
import type { ShopperSearch } from '@/scapi';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useLoadMoreProducts } from '@/hooks/use-load-more-products';
import { getSearchWithoutFiltersParam } from '@/hooks/use-filters-panel-state';
import { uiConfig } from '@/lib/config.ui';
import type { ProductListingProps } from '../product-listing';

type ProductSearchHit = ShopperSearch.schemas['ProductSearchHit'];
type ProductSearchResult = ShopperSearch.schemas['ProductSearchResult'];

const EMPTY_PRODUCTS = Promise.resolve([] as ProductSearchHit[]);
const LOAD_MORE_STORAGE_KEY = 'sfnext:loadMore';

export interface UseProductListingOptions {
    result: ProductSearchResult;
    deferredProducts?: Promise<ProductSearchHit[]>;
    refine: string[];
    query?: string;
    currency: string;
    /** Identifies the listing (e.g. category + currency + locale); the URL search is appended to it. */
    identity: string;
    initialCount?: number;
    offset?: number;
}

export type ProductListingState = Pick<
    ProductListingProps,
    | 'productGridDataKey'
    | 'result'
    | 'isProductGridLoading'
    | 'limit'
    | 'appended'
    | 'firstNewIndex'
    | 'firstNewItemRef'
    | 'appendPending'
    | 'isRestoring'
    | 'restorationTarget'
    | 'loadMore'
    | 'deferredSkeletonCount'
> & { deferredProducts: Promise<ProductSearchHit[]> };

/** Load-more, grid loading and focus state for `<ProductListing>`, shared by the category and search pages. */
export function useProductListing({
    result,
    deferredProducts = EMPTY_PRODUCTS,
    refine,
    query,
    currency,
    identity,
    initialCount: initialCountOption,
    offset = 0,
}: UseProductListingOptions): ProductListingState {
    const config = useConfig();
    const location = useLocation();
    const navigation = useNavigation();
    const searchWithoutFiltersParam = useMemo(() => getSearchWithoutFiltersParam(location.search), [location.search]);
    const productGridDataKey = `${identity}-${searchWithoutFiltersParam}`;

    const isProductGridLoading = useMemo(() => {
        if (navigation.state === 'idle' || !navigation.location) {
            return false;
        }
        const currentRefines = new URLSearchParams(location.search).getAll('refine');
        const nextRefines = new URLSearchParams(navigation.location.search).getAll('refine');
        return (
            currentRefines.length !== nextRefines.length ||
            currentRefines.some((currentRefine, index) => currentRefine !== nextRefines[index])
        );
    }, [location.search, navigation.location, navigation.state]);

    const paginationConfig = uiConfig.pages.category.pagination;
    const criticalCount = result.hits?.length ?? 0;
    const initialCount = initialCountOption ?? criticalCount;

    const {
        appended,
        loadedCount,
        total,
        hasMore,
        isLoading,
        isRestoring,
        restorationTarget,
        firstNewIndex,
        loadMore,
    } = useLoadMoreProducts({
        refine,
        query,
        sort: result.selectedSortingOption,
        currency,
        initialCount,
        total: result.total,
        batchSize: paginationConfig.batchSize,
        mobileBatchSize: paginationConfig.mobileBatchSize,
        maxProducts: paginationConfig.maxProducts,
        identity: productGridDataKey,
        offset,
    });

    const firstNewItemRef = useRef<HTMLElement>(null);
    const focusTargetKey = firstNewIndex === null ? null : `${appended.length}-${firstNewIndex}`;
    useEffect(() => {
        if (focusTargetKey === null || !firstNewItemRef.current) {
            return;
        }
        const node = firstNewItemRef.current;
        node.setAttribute('tabindex', '-1');
        node.focus({ preventScroll: true });
        const onBlur = () => node.removeAttribute('tabindex');
        node.addEventListener('blur', onBlur, { once: true });
        return () => node.removeEventListener('blur', onBlur);
    }, [focusTargetKey]);

    // Persist the loaded count per history entry so `useLoadMoreProducts` can restore it on back-nav.
    useEffect(() => {
        if (typeof window === 'undefined' || isRestoring) {
            return;
        }
        const historyKey = (window.history.state as { key?: string } | null)?.key;
        if (!historyKey) return;
        try {
            const stored = JSON.parse(sessionStorage.getItem(LOAD_MORE_STORAGE_KEY) || '{}');
            if (loadedCount > initialCount) {
                stored[historyKey] = loadedCount;
            } else {
                delete stored[historyKey];
            }
            sessionStorage.setItem(LOAD_MORE_STORAGE_KEY, JSON.stringify(stored));
        } catch {
            // sessionStorage full or unavailable - non-critical, skip
        }
    }, [isRestoring, loadedCount, initialCount]);

    return {
        productGridDataKey,
        result,
        isProductGridLoading,
        limit: config.search.products.hits.limit,
        appended,
        firstNewIndex,
        firstNewItemRef,
        appendPending: isLoading,
        isRestoring,
        restorationTarget,
        loadMore: { loadedCount, total, hasMore, isLoading, onLoadMore: loadMore },
        deferredProducts,
        deferredSkeletonCount: Math.max(0, initialCount - criticalCount),
    };
}
